import type { Translations, Language } from './types';

/**
 * 中文翻译
 */
const zh: Translations = {
  nav: {
    home: '首页',
    features: '功能特性',
    useCases: '使用场景',
    screenshots: '产品截图',
    faq: '常见问题',
  },

  hero: {
    title: 'Page Picker - 强大的网页数据提取工具',
    subtitle: '无需编程，一键提取网页数据',
    cta: '立即安装',
    download: '直接下载',
    badges: ['100% 本地处理', '无需编程', '智能识别'],
    chromeStore: 'Chrome 应用商店',
    edgeStore: 'Edge 应用商店',
    installGuideTitle: '安装说明',
    installGuideNote: '支持 Chrome 和 Edge 浏览器',
    installGuideSteps: [
      '解压下载的 ZIP 文件',
      '打开浏览器，进入 设置 → 扩展程序 → 管理扩展程序',
      '开启右上角的「开发者模式」',
      '点击「加载未打包的扩展程序」',
      '选中刚才解压的文件夹，即可完成安装',
    ],
  },

  features: {
    title: '核心功能',
    items: [
      {
        title: '列表提取',
        description: '点选页面元素即可自动识别列表结构，无需手写选择器。支持自动滚动翻页或点击下一页按钮翻页，实时显示提取进度，可随时停止。',
        tagline: '点选即提取，自动翻页',
        keywords: ['自动翻页', '实时进度', '随时停止'],
      },
      {
        title: '批量详情提取',
        description: '给出一批 URL，点选页面元素指定要提取的内容，后台自动逐一访问并批量抓取。支持手动输入、CSV 导入或从已有提取结果中选取 URL。',
        tagline: '批量抓取多页详情',
        keywords: ['CSV 导入', '后台运行', 'URL 批量'],
      },
      {
        title: '图片提取',
        description: '一键扫描当前页面全部图片，按图片尺寸分组展示，方便筛选。支持单张保存，或按尺寸批量打包下载为 ZIP 格式。',
        tagline: '一键扫描并下载图片',
        keywords: ['尺寸分组', 'ZIP 下载', '批量保存'],
      },
      {
        title: '邮箱提取',
        description: '一键扫描当前页面，自动识别所有邮箱地址。也支持输入多个 URL 后台批量扫描，可配置并发数和扫描深度，支持手动输入、CSV 导入或从已有结果选取 URL。',
        tagline: '批量采集邮箱地址',
        keywords: ['深度扫描', '并发控制', 'CSV 导入'],
      },
      {
        title: '结果管理与导出',
        description: '表格展示提取结果，支持内联图片预览和全局搜索。可在线编辑单元格、重命名或删除列，并过滤隐藏空列、空行、重复值列。支持导出为 Excel（含嵌入图片）、CSV、JSON 或复制到剪贴板，图片可批量打包为 ZIP，历史记录随时切换查看。',
        tagline: '灵活管理与多格式导出',
        keywords: ['Excel / CSV', '图片内嵌', '在线编辑'],
      },
      {
        title: '国际化支持',
        description: '支持中英文双语界面，自动识别浏览器语言，也可手动切换。为全球用户提供本地化体验。',
        tagline: '中英双语，自动适配',
        keywords: ['自动识别', '手动切换', '本地化'],
      },
    ],
  },

  useCases: {
    title: '应用场景',
    items: [
      {
        title: '电商领域',
        items: ['竞品分析', '价格监控', '评论抓取', '商品信息采集'],
      },
      {
        title: '市场调研',
        items: ['趋势跟踪', '社媒数据分析', '行业报告', '用户反馈收集'],
      },
      {
        title: '招聘分析',
        items: ['职位聚合', '薪资分析', '企业情报', '人才市场洞察'],
      },
      {
        title: '学术研究',
        items: ['论文元数据提取', '引用分析', '文献综述', '数据集构建'],
      },
      {
        title: '内容创作',
        items: ['素材收集', '灵感获取', '资源整理', '内容策划'],
      },
      {
        title: '线索挖掘',
        items: ['邮箱采集', '合作伙伴发现', '活动推广', '客户开发'],
      },
    ],
  },

  screenshots: {
    title: '产品截图',
    items: [
      {
        title: '插件首页',
        description: '简洁直观的主界面，快速访问所有核心功能',
      },
      {
        title: '列表提取',
        description: '智能识别列表结构，可视化字段配置',
      },
      {
        title: '详情提取',
        description: '批量详情页抓取，支持深度配置和并发控制',
      },
      {
        title: '详情提取 - 并发配置',
        description: '灵活的并发设置，平衡效率与稳定性',
      },
      {
        title: '图片提取',
        description: '一键扫描并批量下载页面图片',
      },
      {
        title: '邮箱提取',
        description: '智能邮箱识别，支持深度抓取',
      },
      {
        title: '邮箱提取 - 深度并发配置',
        description: '高级配置选项，控制抓取深度和并发数',
      },
      {
        title: '结果页',
        description: '清晰展示提取结果，支持在线编辑',
      },
      {
        title: '结果页 - 过滤条件',
        description: '强大的筛选和排序功能',
      },
      {
        title: '结果页 - 导出选项',
        description: '多种导出格式，满足不同需求',
      },
    ],
  },

  faq: {
    title: '常见问题',
    items: [
      {
        question: 'Page Picker 支持哪些网站？',
        answer: 'Page Picker 支持几乎所有具有列表结构的网站，包括电商平台、社交媒体、新闻网站、招聘网站等。只要页面有规律的数据结构，就可以使用 Page Picker 进行提取。',
      },
      {
        question: '提取的数据存储在哪里？',
        answer: '所有数据都存储在您的浏览器本地（IndexedDB），不会上传到任何服务器。您完全掌控自己的数据，可以随时查看、编辑或删除。',
      },
      {
        question: 'Page Picker 可以抓取登录后的页面吗？',
        answer: '可以。Page Picker 在您已登录的浏览器环境中运行，可以访问需要登录才能查看的页面。我们不会记录您的账号信息，所有操作都在本地完成。',
      },
      {
        question: '为什么有时候提取会失败？',
        answer: '提取失败可能有以下原因：1) 页面使用了动态加载，需要等待内容完全加载；2) 网站有反爬机制；3) 并发数设置过高。建议：等待页面完全加载后再提取，手动滚动页面触发懒加载，或降低并发数量。',
      },
      {
        question: 'Page Picker 支持哪些导出格式？',
        answer: 'Page Picker 支持导出为 Excel 格式（.xlsx），可以使用 Microsoft Excel、Google Sheets、WPS Office、LibreOffice 等软件打开。图片支持批量打包为 ZIP 格式下载。',
      },
    ],
  },

  footer: {
    description: 'Page Picker 是一款强大的网页数据提取工具，无需编程即可轻松获取结构化数据。',
    copyright: '© 2024 Page Picker. 保留所有权利。',
    cta: '立即安装',
    download: '直接下载',
    contact: '联系与支持',
  },
};

/**
 * 英文翻译
 */
const en: Translations = {
  nav: {
    home: 'Home',
    features: 'Features',
    useCases: 'Use Cases',
    screenshots: 'Screenshots',
    faq: 'FAQ',
  },

  hero: {
    title: 'Page Picker - Powerful Web Data Extraction Tool',
    subtitle: 'Extract web data with one click, no coding required',
    cta: 'Install Now',
    download: 'Direct Download',
    badges: ['100% Local Processing', 'No Coding Required', 'Smart Recognition'],
    chromeStore: 'Chrome Web Store',
    edgeStore: 'Edge Add-ons Store',
    installGuideTitle: 'Installation Guide',
    installGuideNote: 'Supports Chrome and Edge browsers',
    installGuideSteps: [
      'Unzip the downloaded ZIP file',
      'Open your browser and go to Settings → Extensions → Manage Extensions',
      'Enable "Developer mode" in the top-right corner',
      'Click "Load unpacked"',
      'Select the unzipped folder to complete the installation',
    ],
  },

  features: {
    title: 'Core Features',
    items: [
      {
        title: 'List Extraction',
        description: 'Click any element to auto-detect list structure — no selectors needed. Supports auto-scroll pagination or click-next-page, with real-time progress you can stop at any time.',
        tagline: 'Click to extract, auto-paginate',
        keywords: ['Auto Pagination', 'Live Progress', 'Stop Anytime'],
      },
      {
        title: 'Batch Detail Extraction',
        description: 'Provide a list of URLs, click to select the fields to extract, and the extension visits and scrapes each page automatically. Supports manual input, CSV import, or URLs from existing results.',
        tagline: 'Scrape details from many URLs',
        keywords: ['CSV Import', 'Background Run', 'Batch URLs'],
      },
      {
        title: 'Image Extraction',
        description: 'One-click scan all images on the current page, grouped by size for easy filtering. Save images individually or batch-download by size group as a ZIP file.',
        tagline: 'Scan & download images in bulk',
        keywords: ['Group by Size', 'ZIP Download', 'Batch Save'],
      },
      {
        title: 'Email Extraction',
        description: 'One-click scan the current page to collect all email addresses. Also supports bulk scanning of multiple URLs with configurable concurrency and depth, via manual input, CSV import, or existing results.',
        tagline: 'Collect emails from any page',
        keywords: ['Deep Scan', 'Concurrency', 'CSV Import'],
      },
      {
        title: 'Results Management & Export',
        description: 'Table view with inline image preview and full-text search across all fields. Inline cell editing, column rename/delete, and filters to hide empty columns, empty rows, or duplicate-value columns. Export as Excel (with embedded images), CSV, JSON, or copy to clipboard. Batch download images as ZIP and switch between past extraction tasks anytime.',
        tagline: 'Manage and export flexibly',
        keywords: ['Excel / CSV', 'Embedded Images', 'Inline Edit'],
      },
      {
        title: 'Internationalization',
        description: 'Bilingual interface in Chinese and English with automatic browser language detection and manual switching. Localized experience for global users.',
        tagline: 'Chinese & English, auto-detected',
        keywords: ['Auto Detect', 'Manual Switch', 'Localized'],
      },
    ],
  },

  useCases: {
    title: 'Use Cases',
    items: [
      {
        title: 'E-commerce',
        items: ['Competitor Analysis', 'Price Monitoring', 'Review Scraping', 'Product Data Collection'],
      },
      {
        title: 'Market Research',
        items: ['Trend Tracking', 'Social Media Analysis', 'Industry Reports', 'User Feedback Collection'],
      },
      {
        title: 'Recruitment Analysis',
        items: ['Job Aggregation', 'Salary Analysis', 'Company Intelligence', 'Talent Market Insights'],
      },
      {
        title: 'Academic Research',
        items: ['Paper Metadata Extraction', 'Citation Analysis', 'Literature Review', 'Dataset Construction'],
      },
      {
        title: 'Content Creation',
        items: ['Material Collection', 'Inspiration Gathering', 'Resource Organization', 'Content Planning'],
      },
      {
        title: 'Lead Generation',
        items: ['Email Collection', 'Partner Discovery', 'Event Promotion', 'Customer Development'],
      },
    ],
  },

  screenshots: {
    title: 'Screenshots',
    items: [
      {
        title: 'Extension Home',
        description: 'Clean and intuitive main interface for quick access to all core features',
      },
      {
        title: 'List Extraction',
        description: 'Smart list structure recognition with visual field configuration',
      },
      {
        title: 'Detail Extraction',
        description: 'Batch detail page scraping with deep configuration and concurrency control',
      },
      {
        title: 'Detail Extraction - Concurrency Settings',
        description: 'Flexible concurrency settings to balance efficiency and stability',
      },
      {
        title: 'Image Extraction',
        description: 'Scan and batch download page images with one click',
      },
      {
        title: 'Email Extraction',
        description: 'Smart email recognition with deep crawling support',
      },
      {
        title: 'Email Extraction - Deep Concurrency Settings',
        description: 'Advanced configuration options to control crawling depth and concurrency',
      },
      {
        title: 'Results Page',
        description: 'Clear display of extraction results with online editing support',
      },
      {
        title: 'Results Page - Filter Options',
        description: 'Powerful filtering and sorting capabilities',
      },
      {
        title: 'Results Page - Export Options',
        description: 'Multiple export formats to meet different needs',
      },
    ],
  },

  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'Which websites does Page Picker support?',
        answer: 'Page Picker supports almost all websites with list structures, including e-commerce platforms, social media, news sites, job boards, etc. As long as the page has regular data structures, you can use Page Picker for extraction.',
      },
      {
        question: 'Where is the extracted data stored?',
        answer: 'All data is stored locally in your browser (IndexedDB) and is never uploaded to any server. You have complete control over your data and can view, edit, or delete it at any time.',
      },
      {
        question: 'Can Page Picker scrape pages that require login?',
        answer: 'Yes. Page Picker runs in your logged-in browser environment and can access pages that require authentication. We do not record your account information; all operations are performed locally.',
      },
      {
        question: 'Why does extraction sometimes fail?',
        answer: 'Extraction may fail for the following reasons: 1) The page uses dynamic loading and needs time to fully load; 2) The website has anti-scraping mechanisms; 3) Concurrency settings are too high. Suggestions: Wait for the page to fully load before extracting, manually scroll to trigger lazy loading, or reduce concurrency.',
      },
      {
        question: 'What export formats does Page Picker support?',
        answer: 'Page Picker supports exporting to Excel format (.xlsx), which can be opened with Microsoft Excel, Google Sheets, WPS Office, LibreOffice, etc. Images can be batch downloaded as ZIP format.',
      },
    ],
  },

  footer: {
    description: 'Page Picker is a powerful web data extraction tool that makes it easy to obtain structured data without coding.',
    copyright: '© 2024 Page Picker. All rights reserved.',
    cta: 'Install Now',
    download: 'Direct Download',
    contact: 'Contact & Support',
  },
};

/**
 * 翻译数据集合
 */
export const translations: Record<Language, Translations> = {
  zh,
  en,
};
